/**
 * Date maths for the client's license periods (rows of the Imaxine `license`
 * table). valid_from / valid_to are inclusive calendar dates (YYYY-MM-DD), so a
 * license whose valid_to is today still works today and has 0 days left.
 */

export interface LicensePeriod {
    id: number;
    valid_from: string;
    valid_to: string;
    /** Note from us to the client, shown only on the admin license list */
    comment_for_client?: string | null;
}

export type LicensePeriodState = 'active' | 'upcoming' | 'expired';

export interface LicensePeriodView extends LicensePeriod {
    state: LicensePeriodState;
    /** active: days until valid_to; upcoming: days until valid_from; expired: days since valid_to */
    days: number;
}

export interface LicenseExpiryBanner {
    validTo: string;
    daysLeft: number;
    /** last few days: show the "renew or service will be blocked" wording */
    finalNotice: boolean;
    dismissible: boolean;
}

/** Start warning everyone this many days before the current license ends */
export const LICENSE_EXPIRY_NOTICE_DAYS = 15;
/** Switch to the final "renew or be blocked" wording at this many days left */
export const LICENSE_FINAL_NOTICE_DAYS = 3;
/** The banner can be closed only while more than this many days are left */
export const LICENSE_DISMISSIBLE_ABOVE_DAYS = 5;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Today as YYYY-MM-DD in the browser's local time zone */
export function todayIso(now = new Date()): string {
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${mm}-${dd}`;
}

/** Whole days from `from` to `to` (both YYYY-MM-DD) */
export function daysBetween(from: string, to: string): number {
    const [fy, fm, fd] = from.slice(0, 10).split('-').map(Number);
    const [ty, tm, td] = to.slice(0, 10).split('-').map(Number);
    return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / DAY_MS);
}

export function describePeriod(period: LicensePeriod, today = todayIso()): LicensePeriodView {
    if (daysBetween(today, period.valid_from) > 0) {
        return { ...period, state: 'upcoming', days: daysBetween(today, period.valid_from) };
    }
    if (daysBetween(today, period.valid_to) < 0) {
        return { ...period, state: 'expired', days: daysBetween(period.valid_to, today) };
    }
    return { ...period, state: 'active', days: daysBetween(today, period.valid_to) };
}

/**
 * Banner for the license in force today, or null when none is needed:
 * no active license (the license guard handles that), more than
 * LICENSE_EXPIRY_NOTICE_DAYS left, or a later license is already in place.
 */
export function expiryBannerFor(periods: LicensePeriod[], today = todayIso()): LicenseExpiryBanner | null {
    const views = periods.map(p => describePeriod(p, today));

    // Overlapping active rows: the one that runs longest is the current license
    const current = views
        .filter(v => v.state === 'active')
        .sort((a, b) => b.valid_to.localeCompare(a.valid_to))[0];
    if (!current || current.days > LICENSE_EXPIRY_NOTICE_DAYS) return null;

    // Already renewed: a license that starts after today
    if (views.some(v => v.state === 'upcoming')) return null;

    return {
        validTo: current.valid_to,
        daysLeft: current.days,
        finalNotice: current.days <= LICENSE_FINAL_NOTICE_DAYS,
        dismissible: current.days > LICENSE_DISMISSIBLE_ABOVE_DAYS,
    };
}
